import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, Empty, Modal, Popover, Spin, Table, Tag, Tooltip, notification } from "antd";
import { DeleteOutlined, EditOutlined, PlusOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { QUESTION_TYPE_META } from "pages/SurveyManagement/constants";
import { createBankQuestion, deleteBankQuestion, fetchQuestionBank, updateBankQuestion } from "util/questionBankApi";
import BankQuestionModal from "./BankQuestionModal";
import styles from "./style.module.scss";

const ERRORS = {
  INVALID_QUESTION_TYPE: "surveySettings.bank.error_type",
  QUESTION_TEXT_REQUIRED: "surveySettings.bank.error_text",
  INVALID_RATING_SCALE: "surveySettings.bank.error_scale",
  OPTIONS_REQUIRED: "surveySettings.bank.error_options",
  QUESTION_BANK_LIMIT_REACHED: "surveySettings.bank.error_limit",
  survey_read_only: "surveySettings.knowledge.error_read_only",
};

// The company's question bank: questions defined once and linked into surveys.
const QuestionBankTab = ({ topics, primaryLanguage }) => {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [editing, setEditing] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    const res = await fetchQuestionBank();
    if (res && !res.error) setData(res);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  if (!data) return <Spin />;
  const canEdit = data.can_manage && !data.read_only;

  const fail = (res) =>
    notification.error({
      message: t(
        ERRORS[res?.reason] ||
          (res?.httpStatus === 403 ? "surveySettings.knowledge.error_forbidden" : "surveySettings.knowledge.error_generic")
      ),
      placement: "bottomRight",
    });

  const save = async (question, translations) => {
    setSaving(true);
    const res = editing
      ? await updateBankQuestion(editing.id, question, translations)
      : await createBankQuestion(question, translations);
    setSaving(false);
    if (!res || res.error) {
      fail(res);
      return;
    }
    setModalOpen(false);
    setEditing(null);
    if (editing && res.updated_surveys) {
      notification.success({
        message: t("surveySettings.bank.updated_surveys", { count: res.updated_surveys }),
        placement: "bottomRight",
      });
    }
    load();
  };

  const remove = (item) =>
    Modal.confirm({
      title: t("surveySettings.bank.delete_title"),
      content: t(item.used_in?.length ? "surveySettings.bank.delete_body_used" : "surveySettings.bank.delete_body", {
        count: item.used_in?.length || 0,
      }),
      okText: t("surveySettings.knowledge.delete_ok"),
      okButtonProps: { danger: true },
      cancelText: t("surveySettings.knowledge.cancel"),
      onOk: async () => {
        const res = await deleteBankQuestion(item.id);
        if (!res || res.error) fail(res);
        else load();
      },
    });

  const columns = [
    {
      title: t("surveySettings.bank.col_question"),
      key: "text",
      render: (_, item) => (
        <div>
          <div className={styles.trigger}>{item.question.text}</div>
          {item.question.options?.length ? (
            <div className={styles.response}>{item.question.options.join(" · ")}</div>
          ) : null}
        </div>
      ),
    },
    {
      title: t("surveySettings.bank.col_type"),
      key: "type",
      width: 150,
      render: (_, item) => {
        const meta = QUESTION_TYPE_META[item.question.type] || {};
        return <Tag className={styles.kindTag}>{meta.labelKey ? t(meta.labelKey) : item.question.type}</Tag>;
      },
    },
    { title: t("surveySettings.bank.col_topic"), key: "topic", width: 130, render: (_, item) => item.question.topic || "—" },
    {
      title: t("surveySettings.bank.col_used"),
      key: "used",
      width: 140,
      render: (_, item) =>
        item.used_in?.length ? (
          <Popover content={item.used_in.map((s) => <div key={s.id}>{s.title}</div>)}>
            <span className={styles.usedLink}>{t("surveySettings.bank.used_count", { count: item.used_in.length })}</span>
          </Popover>
        ) : (
          <span className={styles.meta}>{t("surveySettings.bank.unused")}</span>
        ),
    },
    {
      title: t("surveySettings.knowledge.col_updated"),
      key: "updated",
      width: 150,
      render: (_, item) => (
        <span className={styles.meta}>
          {item.updated_by_email || "—"}
          <br />
          {item.updated_at ? dayjs(item.updated_at).format("D MMM YYYY HH:mm") : ""}
        </span>
      ),
    },
    ...(canEdit
      ? [
          {
            key: "actions",
            width: 90,
            render: (_, item) => (
              <span>
                <Tooltip title={t("surveySettings.knowledge.edit")}>
                  <Button
                    type="text"
                    icon={<EditOutlined />}
                    onClick={() => {
                      setEditing(item);
                      setModalOpen(true);
                    }}
                  />
                </Tooltip>
                <Tooltip title={t("surveySettings.knowledge.delete")}>
                  <Button type="text" danger icon={<DeleteOutlined />} onClick={() => remove(item)} />
                </Tooltip>
              </span>
            ),
          },
        ]
      : []),
  ];

  return (
    <div>
      <div className={styles.toolbar}>
        <p className={styles.intro}>{t(canEdit ? "surveySettings.bank.intro" : "surveySettings.bank.intro_read_only")}</p>
        {canEdit ? (
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => {
              setEditing(null);
              setModalOpen(true);
            }}
          >
            {t("surveySettings.bank.add")}
          </Button>
        ) : null}
      </div>
      <Table
        rowKey="id"
        size="small"
        columns={columns}
        dataSource={data.questions || []}
        pagination={false}
        locale={{ emptyText: <Empty description={t("surveySettings.bank.empty")} /> }}
      />
      <BankQuestionModal
        open={modalOpen}
        item={editing}
        topics={topics}
        primaryLanguage={primaryLanguage}
        saving={saving}
        onCancel={() => {
          setModalOpen(false);
          setEditing(null);
        }}
        onSave={save}
      />
    </div>
  );
};

export default QuestionBankTab;
